import { Component, Input, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonService } from '../common.service';
import { GoogleAnalyticsService } from '../google-analytics.service';
import { EntryStatusComponent } from '../entry-status/entry-status.component';
import { SequenceCardComponent } from '../sequence-card/sequence-card.component';
import { Subscription } from 'rxjs/internal/Subscription';
import { PlatformService } from '../platform.service';
import { PROVIDER_MAP, ProviderData } from '../entry/structure-data.models';
import { ConfigService } from '../config.service';

@Component({
    selector: 'search-result-card',
    templateUrl: './search-result-card.component.html',
    styleUrls: ['./search-result-card.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SearchResultCardComponent implements OnInit {

  @Input() resultRec: any;
  //@Input() isSequenceSearch: boolean;
  @Input() resultRecIndex: number;
  kbCount: number;
  isSequenceSearch: boolean = false;
  routeSubscriber: Subscription;
  dropdownOptions: { label: string, value: any, isShow: boolean }[];

  constructor(private changeDetectorRef: ChangeDetectorRef, public commonService: CommonService, public router: Router, public gaService: GoogleAnalyticsService, 
  private configService: ConfigService,
  private route: ActivatedRoute,  private platform: PlatformService) { }

  @Input() isSelected: boolean = false;
  @Input() disabled: boolean = false;
  @Input() isLimitReached: boolean = false;
  
  @Output() selectionChanged = new EventEmitter<{ value: string, selected: boolean }>();
  
  public isBrowser: boolean = false;
  public providerData = { providerName: '' }; 
  public providerName:string = "";
  public uniprotBaseUrl = this.configService.getConfig().uniprotEntryUrl;
  // Temporary backend workaround: set to false when Solr ordering is fixed.
  private useDisplayOrderingFallback = true;
  public orderedUniprotDescriptions: string[] = [];
  public orderedGenes: string[] = [];
  public orderedTaxIds: number[] = [];
  public orderedUniprotAccessions: string[] = [];
  public orderedOrganismScientificNames: string[] = [];

  notifySelectionChange(event: Event) {
    const checkbox = event.target as HTMLInputElement;
    this.selectionChanged.emit({ value: checkbox.value, selected: checkbox.checked });
  }

  ngOnInit(): void {
     this.isBrowser = this.platform.isBrowserPlatform()
    this.orderedUniprotDescriptions = this.getOrderedField('uniprotDescription');
    this.orderedGenes = this.getOrderedField('gene');
    this.orderedTaxIds = this.getOrderedField('taxId');
    this.orderedUniprotAccessions = this.getOrderedField('uniprotAccession');
    this.orderedOrganismScientificNames = this.getOrderedField('organismScientificName');

    //Get structure count in PDBe-KB
    if(this.orderedUniprotAccessions && this.orderedUniprotAccessions.length > 0) {
      const apiUrl = `https://www.ebi.ac.uk/pdbe/search/pdb/select?group=true&group.field=pdb_id&group.ngroups=true&rows=0&omitHeader=true&json.nl=map&q=uniprot_accession:${this.orderedUniprotAccessions[0]}&wt=json`;
      this.commonService.getApiData(apiUrl).subscribe(apiData => {
        if(apiData && apiData.grouped && apiData.grouped.pdb_id) {
          this.kbCount = apiData.grouped.pdb_id.ngroups;
          this.changeDetectorRef.detectChanges();
        }
      });
    }

    this.providerName = PROVIDER_MAP[this.resultRec.providerId];
    // this.providerData = {
    //   providerName: providerName?? 'Unknown Provider'
    // }
    this.routeSubscriber = this.route.params.subscribe(params => {
      if(params.field === "sequence"){
        this.isSequenceSearch = true;
      }
    });
  }

  // getProviderName(){
  //   const providerName = PROVIDER_MAP[this.resultRec.providerId];
  //   this.providerData = {
  //     providerName: providerName?? 'Unknown Provider'
  //   }
  // }

   formatComplexCompositionSentence(data: any[]): string {
    if (!data || data.length === 0) return '';

    return data.map(item => {
      const unit = item.stoichiometry > 1 ? 'copies' : 'copy';
      return `${item.stoichiometry} ${unit} of ${item.identifier}`;
    }).join(', ');
  }

  generate_download_accessions(entry: string): string {
    return entry;
  }
  
  getPlddtLevel(plddt: number): string {
    return plddt > 90 ? 'Very high' :
           plddt > 70 ? 'High' :
           plddt > 50 ? 'Low' :
           'Very Low';
  }
  
  // Very high (ipSAE ≥ 0.8) – The predicted homodimer interface is modelled with high accuracy.
  // Confident (0.8 > ipSAE ≥ 0.7) – The predicted interface is likely correct with a well-arranged interaction.
  // Low (0.7 > ipSAE ≥ 0.6) – The interaction may be correct but should be interpreted cautiously.
  // Very low (ipSAE < 0.6) 
  getipSAELevel(ipSAE: number): string {
    return ipSAE >= 0.8 ? 'Very high' :  
          ipSAE >= 0.7 ? 'Confident' : 
          ipSAE >= 0.6 ? 'Low' :       
          'Very low';                  
  }
  ngOnDestroy(): void {
    this.routeSubscriber.unsubscribe();
  }

  getEntryTypeForGa(): 'monomer' | 'multimer' {
    return this.resultRec?.isComplex ? 'multimer' : 'monomer';
  }

  getAssemblyTypeForGa(): 'homo' | 'hetero' | 'mono' {
    if (!this.resultRec?.isComplex) {
      return 'mono';
    }
    const assemblyType = (this.resultRec?.assemblyType || '').toLowerCase();
    if (assemblyType === 'homo' || assemblyType === 'hetero') {
      return assemblyType;
    }
    return 'mono';
  }

  trackSearchResultClick(): void {
    this.gaService.emitEventWithParams('search_result_click', {
      entry_type: this.getEntryTypeForGa(),
      assembly_type: this.getAssemblyTypeForGa(),
    });
  }

  private getOrderedField(fieldName: string): any[] {
    if (this.useDisplayOrderingFallback) {
      const displayFieldValue = this.resultRec?.[`${fieldName}_display`];
      if (Array.isArray(displayFieldValue) && displayFieldValue.length > 0) {
        return displayFieldValue;
      }
    }

    const fieldValue = this.resultRec?.[fieldName];
    if (Array.isArray(fieldValue)) {
      return fieldValue;
    }
    if (fieldValue === undefined || fieldValue === null) {
      return [];
    }
    return [fieldValue];
  }
}